import { ChainEnv } from '@vertex-protocol/contracts';
import { NadoSymbolRow } from 'client/hooks/query/markets/nadoSymbols/types';

export const INK_ISOLATED_ONLY_PERP_PRODUCT_IDS = new Set([
  88, 90, 92, 94, 96, 98, 100, 102, 104, 106, 108, 110, 112, 114,
]);

const NADO_SYMBOLS_ENDPOINT_BY_CHAIN_ENV: Partial<Record<ChainEnv, string>> = {
  inkMainnet: 'https://gateway.prod.nado.xyz/v1/symbols',
  inkTestnet: 'https://gateway.test.nado.xyz/v1/symbols',
};

export function parseNadoSymbolsResponse(value: unknown): NadoSymbolRow[] {
  if (!Array.isArray(value)) {
    throw new Error('Invalid Nado symbols response');
  }

  return value.flatMap((row): NadoSymbolRow[] => {
    if (!isRecord(row)) {
      return [];
    }

    const type = row.type;
    const productId = row.product_id;

    if (
      typeof type !== 'string' ||
      typeof productId !== 'number' ||
      !Number.isFinite(productId)
    ) {
      return [];
    }

    return [
      {
        type,
        productId,
        symbol: typeof row.symbol === 'string' ? row.symbol : undefined,
        tradingStatus:
          typeof row.trading_status === 'string'
            ? row.trading_status
            : undefined,
        isolatedOnly: row.isolated_only === true,
      },
    ];
  });
}

export function getIsolatedOnlyProductIds(rows: NadoSymbolRow[]) {
  return new Set(
    rows
      .filter((row) => row.type === 'perp' && row.isolatedOnly)
      .map((row) => row.productId),
  );
}

export function getFallbackIsolatedOnlyProductIds(chainEnv: ChainEnv) {
  if (chainEnv === 'inkMainnet' || chainEnv === 'inkTestnet') {
    return INK_ISOLATED_ONLY_PERP_PRODUCT_IDS;
  }

  return new Set<number>();
}

export async function fetchNadoSymbols(
  chainEnv: ChainEnv,
): Promise<NadoSymbolRow[] | undefined> {
  const endpoint = NADO_SYMBOLS_ENDPOINT_BY_CHAIN_ENV[chainEnv];
  if (!endpoint) {
    return undefined;
  }

  const response = await fetch(endpoint);
  if (!response.ok) {
    throw new Error(`Nado symbols request failed: ${response.status}`);
  }

  return parseNadoSymbolsResponse(await response.json());
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null;
}
